import api from '../services/api';

const OTP_CONFIG = {
  LENGTH: 6,
  EXPIRY_MINUTES: 10,
  MAX_ATTEMPTS: 5,
  RESEND_COOLDOWN: 60 // seconds
};

// Send OTP to the provided email
export const sendVerificationCode = async (email) => {
  try {
    if (!email || !email.includes('@')) {
      throw new Error('Please enter a valid email address');
    }

    const response = await api.post('/users/send-otp/', { email });
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to send verification code');
    }
    return { success: true, message: 'Verification code sent successfully' };
  } catch (error) {
    console.error('Error sending verification code:', error);
    throw new Error(error.response?.data?.message || 'Failed to send verification code');
  }
};

// Verify OTP code
export const verifyCode = async (email, code) => {
  try {
    if (!email || !code) {
      throw new Error('Email and code are required');
    }

    const response = await api.post('/users/verify-otp/', { email, otp: code });
    if (!response.data.success) {
      throw new Error(response.data.message || 'Invalid verification code');
    }
    return { success: true, message: 'Code verified successfully', token: response.data.token };
  } catch (error) {
    console.error('Error verifying code:', error);
    throw new Error(error.response?.data?.message || 'Invalid verification code');
  }
};

// Check email verification status
export const isEmailVerified = async (email) => {
  try {
    if (!email) {
      return { verified: false, message: 'Email is required' };
    }

    // First check if we have a valid token in localStorage
    const token = localStorage.getItem('authToken');
    if (token) {
      return { verified: true, message: 'Email is verified' };
    }

    return { verified: false, message: 'Email not verified' };
  } catch (error) {
    console.error('Error checking email verification status:', error);
    return { verified: false, message: 'Error checking verification status' };
  }
};

// Register user with verified email
export const registerWithEmailVerification = async (email, password, userData) => {
  try {
    if (!email || !password) {
      throw new Error('Email and password are required');
    }

    // Verify email is verified
    const verificationStatus = await isEmailVerified(email);
    if (!verificationStatus.verified) {
      throw new Error(verificationStatus.message || 'Email must be verified before registration');
    }

    // Since we're using our custom backend, we'll just store the user data
    // and return it with a success message
    const user = {
      email,
      ...userData,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    return {
      success: true,
      user,
      message: 'User registered successfully'
    };
  } catch (error) {
    console.error('Error registering user:', error);
    throw new Error(error.message || 'Registration failed');
  }
};

// Clean up verification code
export const cleanupVerification = async (email) => {
  try {
    localStorage.removeItem('authToken');
    return { success: true, message: 'Verification cleaned up' };
  } catch (error) {
    console.error('Error cleaning up verification:', error);
    return { success: false, message: 'Error cleaning up verification' };
  }
};

// Resend verification code
export const resendCode = async (email) => {
  try {
    if (!email) {
      throw new Error('Email is required');
    }

    // Check if we're within cooldown period
    const token = localStorage.getItem('authToken');
    if (token) {
      throw new Error('Email is already verified');
    }

    return await sendVerificationCode(email);
  } catch (error) {
    console.error('Error resending code:', error);
    throw new Error(error.message || 'Failed to resend verification code');
  }
};

